import { useEffect, useState, useCallback } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { emitCustomEvent } from 'react-custom-events'
import { getEditableFields } from '~/lib/api'
import { showErrorModal } from '~/utils'
import * as styles from './EditFieldsExpirationWarning.module.css'

const WARNING_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes

function formatTimeRemaining(ms) {
  const minutes = Math.floor(ms / 60000)
  const seconds = Math.floor((ms % 60000) / 1000)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export default function EditFieldsExpirationWarning() {
  const { t } = useTranslation()
  const { values, setFieldValue } = useFormikContext()
  const [timeRemaining, setTimeRemaining] = useState(null)
  const [showWarning, setShowWarning] = useState(false)
  const [hasExpired, setHasExpired] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)

  const validUntil = values.editableFieldsValidUntil

  const handleExpiration = useCallback(async () => {
    if (hasExpired) return // Already handled

    // Set hasExpired immediately to prevent duplicate calls
    // (interval might call this multiple times before API responds)
    setHasExpired(true)

    console.log('=== Edit fields validUntil has expired ===')
    console.log('Fetching updated editable fields from API...')

    try {
      // Fetch the updated editable fields from the API
      const bookingId = values.bookingId
      if (!bookingId) {
        console.error('No booking ID available')
        return
      }

      const editableData = await getEditableFields(bookingId)
      console.log('=== API Response ===')
      console.log('Updated editable fields data:', editableData)
      console.log('New validUntil:', editableData.validUntil)

      // Get the previous and new editable fields
      const previousEditableFields = values.editableFields || []
      const newEditableFields = editableData.editableFields || []

      console.log('Previous editable fields count:', previousEditableFields.length, previousEditableFields)
      console.log('New editable fields count:', newEditableFields.length, newEditableFields)

      // Check if no fields are editable anymore
      if (newEditableFields.length === 0) {
        console.log('No fields are editable - showing confirmation and exiting edit mode')

        // Define the reset function to exit edit mode
        const exitEditMode = () => {
          console.log('Exiting edit mode - triggering booking module close sequence')
          // Use setTimeout to ensure any modal is fully closed before triggering events
          setTimeout(() => {
            console.log('Triggering BOOKING_MODULE_SHOW_COVER')
            if (typeof APP !== 'undefined' && APP.trigger && APP.events) {
              APP.trigger(APP.events.BOOKING_MODULE_SHOW_COVER)
            } else {
              console.warn('APP or APP.events not available')
            }
            console.log('Emitting RESET_FORM')
            emitCustomEvent('RESET_FORM')
            console.log('Emitting RESET_MAP')
            emitCustomEvent('RESET_MAP')
          }, 50)
        }

        const message = `<strong>${t('edit-fields-no-changes-title')}</strong><br/><br/>${t('edit-fields-no-changes-message')}`
        showErrorModal(message, exitEditMode)

        // Clear validUntil to stop the polling timer
        // This prevents the timer from triggering again
        setFieldValue('editableFieldsValidUntil', null, false)
        return
      }

      // Find fields that were editable but are no longer editable
      const disabledFields = previousEditableFields.filter(
        field => !newEditableFields.includes(field)
      )

      console.log('Fields that got disabled:', disabledFields)

      // Get the original booking data
      const originalBooking = values.booking
      if (!originalBooking) {
        console.error('No original booking data available')
        return
      }

      // Map of field paths to their original values
      const fieldMapping = {
        'traveler.name': originalBooking.traveler?.name || '',
        'traveler.phoneNumber': originalBooking.traveler?.phoneNumber || '',
        'generalComment': originalBooking.generalComment || '',
        'invoiceReference': originalBooking.invoiceReference || '',
        'originLocation.flightInfo.flightNumber': originalBooking.originLocation?.flightInfo?.flightNumber || '',
        'originLocation.flightInfo.checkedInBaggage': originalBooking.originLocation?.flightInfo?.checkedInBaggage || false,
        'pickUpTimeStamp': originalBooking.pickUpTimeStamp || '',
      }

      // Map of field paths to form field names
      const formFieldMapping = {
        'traveler.name': 'name',
        'traveler.phoneNumber': 'phone',
        'generalComment': 'generalComment',
        'invoiceReference': 'accountReference',
        'originLocation.flightInfo.flightNumber': 'flightNumber',
        'originLocation.flightInfo.checkedInBaggage': 'checkedInBaggage',
        'pickUpTimeStamp': 'pickupTime',
      }

      // Revert only the fields that were disabled and have been changed
      disabledFields.forEach(fieldPath => {
        if (fieldMapping[fieldPath] !== undefined) {
          const formFieldName = formFieldMapping[fieldPath]
          let originalValue = fieldMapping[fieldPath]
          const currentValue = values[formFieldName]

          // Convert pickUpTimeStamp string to Date object for pickupTime field
          if (fieldPath === 'pickUpTimeStamp' && originalValue) {
            originalValue = new Date(originalValue)
          }

          // For time fields, compare timestamps instead of direct equality
          const valuesAreDifferent = fieldPath === 'pickUpTimeStamp'
            ? new Date(currentValue).getTime() !== new Date(originalValue).getTime()
            : currentValue !== originalValue

          if (valuesAreDifferent) {
            console.log(`Reverting ${formFieldName} from "${currentValue}" to "${originalValue}"`)
            setFieldValue(formFieldName, originalValue, false)
          }
        }
      })

      // Update to the new editable fields list
      setFieldValue('editableFields', newEditableFields, false)

      // Update editable options (vehicle options, etc.) if provided
      if (editableData.editableOptions !== undefined) {
        setFieldValue('editableOptions', editableData.editableOptions, false)
      }

      // Update the new validUntil timestamp if provided
      if (editableData.validUntil) {
        console.log('New validUntil received, will start new timer:', editableData.validUntil)
        setFieldValue('editableFieldsValidUntil', editableData.validUntil, false)
        // Reset hasExpired so the new timer can trigger
        setHasExpired(false)
      } else {
        console.log('No validUntil received, stopping timer - no more polling')
        // Clear validUntil to stop the timer
        setFieldValue('editableFieldsValidUntil', null, false)
        setHasExpired(true)
      }
    } catch (error) {
      console.error('Error fetching updated editable fields:', error)
    }
  }, [hasExpired, values, setFieldValue, t])

  useEffect(() => {
    if (!validUntil) {
      setShowWarning(false)
      // Don't reset hasExpired here - if there's no validUntil, we should stay expired
      return
    }

    // hasExpired is now managed in handleExpiration based on whether a new validUntil is received

    const checkExpiration = () => {
      const now = Date.now()
      const expirationTime = new Date(validUntil).getTime()
      const remaining = expirationTime - now

      setTimeRemaining(remaining)

      if (remaining <= 0) {
        // Time has expired - fetch updated fields
        handleExpiration()
        setShowWarning(false)
      } else if (remaining <= WARNING_THRESHOLD_MS) {
        // Show warning
        setShowWarning(true)
      } else {
        // Not yet time to warn
        setShowWarning(false)
      }
    }

    // Initial check
    checkExpiration()

    // Set up interval to check every second
    const interval = setInterval(checkExpiration, 1000)

    return () => clearInterval(interval)
  }, [validUntil, handleExpiration])

  if (!showWarning || timeRemaining === null || timeRemaining <= 0 || isDismissed) {
    return null
  }

  return (
    <div className={styles.warningBanner}>
      <div className={styles.warningContent}>
        <div className={styles.warningText}>
          <strong>{t('edit-fields-expiration-warning', { time: formatTimeRemaining(timeRemaining) })}</strong>
        </div>
        <button
          className={styles.closeButton}
          onClick={() => setIsDismissed(true)}
          aria-label="Close warning"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  )
}
